import type { LicenseEntitlementDisplayBooleanConfig } from '#layers/license/constants/license-entitlement-display-config';
import type { LicenseEntitlementBooleanKey, LicenseEntitlementNumericKey, LicenseLimit, WorkspaceUsageEntry } from '#layers/license/types/license';
import type { LicenseEntitlementDisplay, LicenseEntitlementPerWorkspaceValue, LicenseEntitlementTextValue, LicenseEntitlementValue, LicenseEntitlementWorkspaceChild } from '#layers/license/types/license-entitlement-display';
import { LICENSE_ENTITLEMENT_DISPLAY_CONFIG } from '#layers/license/constants/license-entitlement-display-config';

const VALUE_PREFIX = 'organization.dashboard.settings.license.value';

interface LicenseEntitlementDisplayGetters {
	getEntitlementLimit: (key: LicenseEntitlementNumericKey) => LicenseLimit;
	hasEntitlement: (key: LicenseEntitlementBooleanKey) => boolean;
}

export function buildLicenseEntitlementDisplay(getters: LicenseEntitlementDisplayGetters): LicenseEntitlementDisplay[] {
	return LICENSE_ENTITLEMENT_DISPLAY_CONFIG.map((config) => ({
		id: config.id,
		icon: config.icon,
		titleKey: config.titleKey,
		value: config.kind === 'numeric'
			? buildNumericValue(getters.getEntitlementLimit(config.id))
			: buildBooleanValue(config, getters.hasEntitlement(config.id)),
	}));
}

function buildNumericValue(limit: LicenseLimit): LicenseEntitlementValue {
	if (limit.allowance === null) {
		return { type: 'text', labelKey: `${VALUE_PREFIX}.unlimited` };
	}

	const allowance = limit.allowance;

	if (limit.perWorkspace !== null) {
		const value: LicenseEntitlementPerWorkspaceValue = {
			type: 'per_workspace',
			labelKey: `${VALUE_PREFIX}.per_workspace`,
			limit: allowance,
			children: limit.perWorkspace.map((entry) => buildWorkspaceChild(entry)),
		};

		if (limit.isOverAllowance) value.overLimit = true;

		return value;
	}

	const value: LicenseEntitlementTextValue = {
		type: 'text',
		labelKey: `${VALUE_PREFIX}.count`,
		usage: limit.usage ?? undefined,
		limit: allowance,
	};

	if (limit.isOverAllowance) {
		value.overLimit = true;
	}

	return value;
}

function buildWorkspaceChild(entry: WorkspaceUsageEntry): LicenseEntitlementWorkspaceChild {
	const child: LicenseEntitlementWorkspaceChild = {
		workspaceId: entry.workspaceId,
		usage: entry.usage,
	};

	if (entry.isOverAllowance) {
		child.overLimit = true;
	}

	return child;
}

function buildBooleanValue(config: LicenseEntitlementDisplayBooleanConfig, has: boolean): LicenseEntitlementValue {
	const included = config.invert ? !has : has;
	const labelKeys = config.valueLabelKeys ?? { included: `${VALUE_PREFIX}.included`, notIncluded: `${VALUE_PREFIX}.not_included` };

	return { type: 'boolean', included, labelKey: included ? labelKeys.included : labelKeys.notIncluded };
}
